import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatViews, getCategoryName } from "@/utils/content";

export default function FocusHero({ featured, latest }: { featured: Article; latest: Article[] }) {
  return (
    <section className="border-b border-background-200 bg-background-50">
      <div className="w-full px-4 pb-10 pt-8 md:px-8 md:pb-14 md:pt-10">
        <div className="flex flex-col gap-2 border-b border-foreground-950/80 pb-4 md:flex-row md:items-end md:justify-between">
          <p className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
            今日焦点 · 材料整理
          </p>
          <p className="text-sm text-foreground-500">
            {new Date().getFullYear()} 年 · 全网热点按事件归类，先看时间线再看结论
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <article className="animate-rise group lg:col-span-2">
            <Link to={`/article/${featured.id}`} className="block cursor-pointer">
              <div className="relative aspect-[16/10] w-full overflow-hidden rounded-lg bg-foreground-900">
                <img
                  src={featured.image}
                  alt={featured.title}
                  title={`${featured.title} - 热点整理`}
                  className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
                      {getCategoryName(featured.categoryId)}
                    </span>
                    <span className="rounded-full bg-background-50/20 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
                      重点追踪
                    </span>
                  </div>
                  <h2 className="mt-3 font-heading text-xl font-bold leading-snug text-background-50 md:text-3xl">
                    {featured.title}
                  </h2>
                  <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 sm:block">
                    {featured.summary}
                  </p>
                </div>
              </div>
            </Link>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="text-foreground-800">{featured.author}</span>
              <span>{featured.date}</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(featured.views)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {featured.readTime} 分钟阅读
              </span>
            </div>
          </article>

          <aside className="flex flex-col">
            <div className="flex items-center justify-between border-b border-background-300 pb-3">
              <h3 className="font-heading text-base font-bold text-foreground-950">最新爆料</h3>
              <Link to="/topics" className="flex items-center gap-0.5 text-xs text-primary-600 transition-colors hover:text-primary-700 cursor-pointer">
                全部
                <i className="ri-arrow-right-s-line text-base"></i>
              </Link>
            </div>
            <div className="flex flex-col">
              {latest.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group flex items-start gap-3 border-b border-background-200 py-4 cursor-pointer last:border-b-0"
                >
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-xs font-bold text-secondary-700">
                    {latest.indexOf(article) + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                      {article.title}
                    </p>
                    <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {formatViews(article.views)}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}